import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  TableSortLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InboxIcon from '@mui/icons-material/Inbox';
import { RequisitionDropdown } from './RequisitionDropdown';

// ── Shared cell styles matching Dockets ──────────────────────────────────────

const headCellSx = {
  color: '#64748b',
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  py: 1.5,
  px: 2,
  borderBottom: 'none',
  whiteSpace: 'nowrap',
  bgcolor: '#fafafa',
};

const bodyCellSx = {
  fontSize: '12px',
  py: 1,
  px: 2,
  borderBottom: '1px solid #f1f5f9',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// ── Status chip identical to Dockets StatusChip ──────────────────────────────

const StatusChip = ({ val }) => {
  if (!val) return <Typography sx={{ color: 'text.disabled', fontSize: 12 }}>—</Typography>;
  const isYes = val === 'Yes';
  return (
    <Chip
      label={val}
      size="small"
      variant="outlined"
      color={isYes ? 'success' : 'default'}
      sx={{ fontSize: 11, height: 22 }}
    />
  );
};

// ── Column definitions (mirrors COLUMNS pattern from Dockets) ────────────────

const COLUMNS = [
  { id: 'deptNumberDoNotEdit', label: 'Dept #', width: 80, sortable: true, mono: true },
  { id: 'deptNumberDeptNameForReporting', label: 'Reporting Name', width: 200, sortable: true },
  { id: 'businessUnitLevel3', label: 'Business Unit (L3)', width: 160, sortable: true },
  { id: 'departmentGroupLevel2', label: 'Dept Group (L2)', width: 150 },
  { id: 'departmentChecksLevel1', label: 'Dept Checks (L1)', width: 150 },
  { id: 'reportingDepartmentToSplitYesNo', label: 'Split?', width: 80, isChip: true },
  { id: 'reportingIncludeYesOrNo', label: 'Include?', width: 80, isChip: true },
  { id: 'requisitionType', label: 'Requisition Type', width: 210, isReq: true },
];

// ── Main component ────────────────────────────────────────────────────────────

export function DepartmentTable({ departments, onEdit, onDelete, onRequisitionChange }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [includeFilter, setIncludeFilter] = useState('ALL');
  const [sortField, setSortField] = useState('deptNumberDoNotEdit');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = field => {
    setSortDir(prev => (sortField === field && prev === 'asc' ? 'desc' : 'asc'));
    setSortField(field);
  };

  const rows = useMemo(() => {
    let r = [...departments];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      r = r.filter(
        d =>
          String(d.deptNumberDoNotEdit).includes(q) ||
          (d.deptNumberDeptNameForReporting || '').toLowerCase().includes(q) ||
          (d.businessUnitLevel3 || '').toLowerCase().includes(q) ||
          (d.departmentGroupLevel2 || '').toLowerCase().includes(q) ||
          (d.departmentChecksLevel1 || '').toLowerCase().includes(q),
      );
    }

    if (includeFilter !== 'ALL') {
      r = r.filter(d => d.reportingIncludeYesOrNo === includeFilter);
    }

    r.sort((a, b) => {
      const va = a[sortField] ?? '';
      const vb = b[sortField] ?? '';
      const numA = Number(va),
        numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB)) return sortDir === 'asc' ? numA - numB : numB - numA;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

    return r;
  }, [departments, searchQuery, includeFilter, sortField, sortDir]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Toolbar (matches Dockets filter bar style) ── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          placeholder="Search dept #, name, group, unit, checks…"
          size="small"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: 320 },
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              borderRadius: '8px',
              bgcolor: '#fff',
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
              '&.Mui-focused fieldset': { borderColor: '#0f172a' },
            },
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Include:
          </Typography>
          <Select
            value={includeFilter}
            onChange={e => setIncludeFilter(e.target.value)}
            size="small"
            sx={{
              fontSize: '13px',
              minWidth: 130,
              borderRadius: '8px',
              bgcolor: '#fff',
              '& fieldset': { borderColor: '#e2e8f0' },
            }}>
            <MenuItem value="ALL" sx={{ fontSize: 13 }}>
              All Records
            </MenuItem>
            <MenuItem value="Yes" sx={{ fontSize: 13 }}>
              Included
            </MenuItem>
            <MenuItem value="No" sx={{ fontSize: 13 }}>
              Excluded
            </MenuItem>
          </Select>
        </Box>
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 1, overflow: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
          <colgroup>
            {COLUMNS.map(c => (
              <col key={c.id} style={{ width: c.width, minWidth: c.width }} />
            ))}
            <col style={{ width: 90 }} />
          </colgroup>

          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell key={col.id} sx={headCellSx}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortField === col.id}
                      direction={sortField === col.id ? sortDir : 'asc'}
                      onClick={() => handleSort(col.id)}>
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
              <TableCell align="right" sx={headCellSx}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <InboxIcon sx={{ fontSize: 36, color: '#cbd5e1', display: 'block', mx: 'auto', mb: 1 }} />
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              rows.map(dept => (
                <TableRow key={dept.deptNumberDoNotEdit} hover sx={{ '&:last-child td': { border: 0 } }}>
                  {COLUMNS.map(col => {
                    const val = dept[col.id];

                    if (col.mono)
                      return (
                        <TableCell
                          key={col.id}
                          sx={{ ...bodyCellSx, fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                          {val ?? '—'}
                        </TableCell>
                      );

                    if (col.isChip)
                      return (
                        <TableCell key={col.id} sx={bodyCellSx}>
                          <StatusChip val={val} />
                        </TableCell>
                      );

                    if (col.isReq)
                      return (
                        <TableCell key={col.id} sx={{ ...bodyCellSx, overflow: 'visible', p: 0 }}>
                          <RequisitionDropdown
                            value={val}
                            deptNumber={dept.deptNumberDoNotEdit}
                            onChange={onRequisitionChange}
                          />
                        </TableCell>
                      );

                    return (
                      <TableCell
                        key={col.id}
                        sx={{ ...bodyCellSx, color: '#475569', maxWidth: col.width }}
                        title={val ?? ''}>
                        {val || '—'}
                      </TableCell>
                    );
                  })}

                  {/* Actions */}
                  <TableCell align="right" sx={{ ...bodyCellSx, overflow: 'visible' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(dept)}
                        title="Edit"
                        sx={{
                          color: '#64748b',
                          border: '1px solid #e2e8f0',
                          bgcolor: '#fff',
                          borderRadius: '6px',
                          p: '3px',
                          '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' },
                        }}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(dept)}
                        title="Delete"
                        sx={{
                          color: '#94a3b8',
                          border: '1px solid #e2e8f0',
                          bgcolor: '#fff',
                          borderRadius: '6px',
                          p: '3px',
                          '&:hover': { color: '#e41e3f', bgcolor: '#fff1f2', borderColor: '#fecdd3' },
                        }}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
